<template>
  <div class="om-scope om-reader">
    <p v-if="state === states.loading" class="om-status">{{ copy.loading }}</p>
    <div v-else-if="state !== states.ready" class="om-card om-status-card">
      <p>{{ state === states.missing ? copy.notAvailable : copy.loadError }}</p>
      <button v-if="state === states.failed" type="button" class="om-button om-button-ghost" @click="load">{{ copy.retry }}</button>
    </div>
    <template v-else>
      <aside class="om-card om-sidebar">
        <button type="button" class="om-index-toggle" :aria-expanded="String(isIndexOpen)" @click="isIndexOpen = !isIndexOpen">
          <span><i class="ni ni-bullet-list-67" aria-hidden="true"></i> {{ copy.index }}</span>
          <i class="ni" :class="isIndexOpen ? 'ni-bold-up' : 'ni-bold-down'" aria-hidden="true"></i>
        </button>
        <div class="om-sidebar-body" :class="{ 'om-sidebar-open': isIndexOpen }">
          <router-link class="om-back" :to="homeRoute"><i class="ni ni-bold-left" aria-hidden="true"></i> {{ copy.backToManual }}</router-link>
          <manual-chapter-list compact :manual-key="manualKey" :chapters="chapters" :active-slug="chapter.slug" @navigate="isIndexOpen = false" />
        </div>
      </aside>

      <article class="om-card om-article">
        <manual-pending-bar compact class="om-article-pending" :manual-key="manualKey" />
        <p class="om-eyebrow">{{ chapter.part_title }} · {{ chapter.version_label }}</p>
        <h1 class="om-article-title">{{ chapter.title }} <manual-change-badge :status="chapter.change_status" /></h1>
        <p v-if="changeNotice" class="om-change-notice"><i class="ni ni-notification-70" aria-hidden="true"></i> {{ changeNotice }}</p>
        <div class="om-prose" v-html="chapter.html"></div>
        <nav class="om-pager">
          <router-link v-if="chapter.previous" class="om-pager-link" :to="chapterRoute(chapter.previous)">
            <span class="om-pager-label"><i class="ni ni-bold-left" aria-hidden="true"></i> {{ copy.previous }}</span>
            <span class="om-pager-title">{{ chapter.previous.title }}</span>
          </router-link>
          <router-link v-if="chapter.next" class="om-pager-link om-pager-next" :to="chapterRoute(chapter.next)">
            <span class="om-pager-label">{{ copy.next }} <i class="ni ni-bold-right" aria-hidden="true"></i></span>
            <span class="om-pager-title">{{ chapter.next.title }}</span>
          </router-link>
        </nav>
        <manual-pending-bar :manual-key="manualKey" />
      </article>
    </template>
  </div>
</template>

<script>
import '@/components/OperationsManual/operations-manual.css';
import ManualChangeBadge from '@/components/OperationsManual/ManualChangeBadge.vue';
import ManualChapterList from '@/components/OperationsManual/ManualChapterList.vue';
import ManualPendingBar from '@/components/OperationsManual/ManualPendingBar.vue';
import { CHANGE_STATUS, MANUAL_COPY, MANUAL_ROUTES } from '@/components/OperationsManual/manualCopy';
import manualLoading from './manualLoading';

const CHANGE_NOTICES = {
  [CHANGE_STATUS.new]: MANUAL_COPY.newChapterNotice,
  [CHANGE_STATUS.updated]: MANUAL_COPY.changedChapterNotice,
};

export default {
  name: 'ManualChapter',
  components: { ManualChangeBadge, ManualChapterList, ManualPendingBar },
  mixins: [manualLoading],
  data() {
    return { copy: MANUAL_COPY, chapter: null, chapters: [], chaptersManualKey: null, isIndexOpen: false };
  },
  computed: {
    changeNotice() {
      return CHANGE_NOTICES[this.chapter.change_status] || '';
    },
    homeRoute() {
      return { name: MANUAL_ROUTES.home, params: { manualKey: this.manualKey } };
    },
  },
  methods: {
    async fetchContent() {
      const manualKey = this.manualKey;
      const [chapter] = await Promise.all([
        this.$store.dispatch('operationsManual/fetchChapter', { manualKey, chapterSlug: this.$route.params.chapterSlug }),
        this.loadChapterIndex(manualKey),
      ]);
      this.chapter = chapter;
      window.scrollTo(0, 0);
      return true;
    },
    async loadChapterIndex(manualKey) {
      if (this.chaptersManualKey === manualKey) return;
      const home = await this.$store.dispatch('operationsManual/fetchHome', manualKey);
      this.chapters = home.chapters;
      this.chaptersManualKey = manualKey;
    },
    chapterRoute(neighbour) {
      return { name: MANUAL_ROUTES.chapter, params: { manualKey: this.manualKey, chapterSlug: neighbour.slug } };
    },
  },
};
</script>

<style scoped>
.om-reader { max-width: 1180px; margin: 0 auto; padding: 22px 16px 40px; display: grid; grid-template-columns: 300px minmax(0, 1fr); gap: 18px; align-items: start; }
.om-sidebar { position: sticky; top: 16px; padding: 14px; max-height: calc(100vh - 32px); overflow-y: auto; }
.om-index-toggle { display: none; }
.om-back { display: inline-flex; align-items: center; gap: 6px; margin-bottom: 14px; font-size: 13px; font-weight: 650; color: var(--om-green-dark); }
.om-article { padding: 24px 28px; min-width: 0; }
.om-article-pending { margin-bottom: 14px; }
.om-article-title { font-size: 24px; font-weight: 750; line-height: 1.25; margin: 0 0 14px; display: flex; flex-wrap: wrap; align-items: center; gap: 10px; color: var(--om-ink); }
.om-change-notice { background: var(--om-amber-tint); border: 1px solid rgba(236, 170, 51, .45); color: #7a5312; border-radius: 10px; padding: 10px 14px; font-size: 14px; margin: 0 0 18px; }
.om-pager { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 28px; padding-top: 18px; border-top: 1px solid var(--om-line); }
.om-pager-link { display: grid; gap: 4px; padding: 12px 14px; border: 1px solid var(--om-line); border-radius: 12px; color: var(--om-ink); text-decoration: none; min-height: 64px; }
.om-pager-link:hover { border-color: var(--om-green); color: var(--om-ink); }
.om-pager-next { grid-column: 2; text-align: right; }
.om-pager-label { font-size: 12px; font-weight: 700; color: var(--om-green-dark); }
.om-pager-title { font-size: 14px; line-height: 1.35; }
@media (max-width: 991px) {
  .om-reader { grid-template-columns: minmax(0, 1fr); padding: 12px 12px 32px; gap: 12px; }
  .om-sidebar { position: static; max-height: none; padding: 0; overflow: visible; }
  .om-index-toggle { display: flex; width: 100%; min-height: 48px; align-items: center; justify-content: space-between; padding: 12px 16px; background: none; border: none; font: inherit; font-weight: 700; color: var(--om-ink); cursor: pointer; }
  .om-sidebar-body { display: none; padding: 0 12px 12px; }
  .om-sidebar-open { display: block; }
  .om-article { padding: 18px 16px; }
  .om-article-title { font-size: 20px; }
  .om-pager { grid-template-columns: minmax(0, 1fr); }
  .om-pager-next { grid-column: 1; }
}
</style>
